import { useDataStoreOptional, usePageContextOptional } from "@judo/core";
import { useModelLabel } from "@judo/i18n";
import type { PageContainer, PageDefinition } from "@judo/model-api";
import { TitleFrom } from "@judo/model-api";
import { useCallback, useSyncExternalStore } from "react";

/**
 * Result of usePageTitle.
 */
export interface PageTitleResult {
	/** Resolved page title string */
	title: string;
	/** Whether title is dynamic (from attribute data) */
	isDynamic: boolean;
}

/**
 * Hook that resolves the page title based on the page container's `titleFrom` configuration.
 *
 * - `titleFrom=LABEL` (default): Uses translated label from `useModelLabel(page)`.
 * - `titleFrom=ATTRIBUTE`: Uses the value of `titleAttribute` from the transfer data store,
 *   falling back to the translated label while data is loading or unavailable.
 *
 * @param page - The page definition
 * @param transferId - Optional transfer ID override (for cases where the caller is outside PageProvider, e.g. dialog titles)
 * @returns PageTitleResult with the resolved title and whether it's dynamic
 */
export function usePageTitle(page: PageDefinition, transferId?: string | null): PageTitleResult {
	const { label: translatedLabel } = useModelLabel(page);
	const container = page.container as PageContainer | undefined;

	const titleFrom = container?.titleFrom;
	const titleAttribute = container?.titleAttribute;
	const isDynamic = titleFrom === TitleFrom.ATTRIBUTE && titleAttribute != null;

	// Resolve transfer ID from context if not provided
	const pageContext = usePageContextOptional();
	const effectiveEntityId = transferId ?? pageContext?.transferId ?? null;

	// Use optional data store - may not be available in all contexts
	const store = useDataStoreOptional();

	// Build a selector for the attribute field
	const selector = useCallback(
		(state: { transfers: Map<string, unknown> }) => {
			if (!isDynamic || !effectiveEntityId) return undefined;
			const transferState = state.transfers.get(effectiveEntityId) as { data?: Record<string, unknown> } | undefined;
			return transferState?.data?.[titleAttribute.name];
		},
		[isDynamic, effectiveEntityId, titleAttribute?.name]
	);

	const subscribe = useCallback(
		(cb: () => void) => {
			if (!store) return () => {};
			return store.subscribe(selector, cb);
		},
		[store, selector]
	);

	const getSnapshot = useCallback(() => {
		if (!store) return undefined;
		return store.getSnapshot(selector);
	}, [store, selector]);

	const attributeValue = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

	// If titleFrom is ATTRIBUTE and we have a non-empty attribute value, use it
	if (isDynamic && attributeValue != null && String(attributeValue) !== "") {
		return {
			title: String(attributeValue),
			isDynamic: true,
		};
	}

	// Fallback: use translated label
	return {
		title: translatedLabel,
		isDynamic: false,
	};
}
